import { useEffect, useState } from "react";
import { X } from "lucide-react";

const HIDDEN_KEY = "ml_visualizer_launcher_hidden";

/**
 * Floating "See our products in your space" launcher — bottom-left.
 * Dark pill label + glowing white circle with hand-holding-phone AR icon.
 */
export default function VisualizerLauncher() {
  const [visible, setVisible] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(HIDDEN_KEY) === "1") setHidden(true);
    } catch {
      /* ignore */
    }
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const open = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("open-visualizer"));
    }
  };

  const dismiss = (e) => {
    e.stopPropagation();
    setHidden(true);
    try {
      sessionStorage.setItem(HIDDEN_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (hidden) return null;

  return (
    <div
      data-testid="visualizer-launcher"
      className={`group fixed z-[85] bottom-40 sm:bottom-8 left-0 transition-all duration-500 ${
        visible
          ? "translate-x-0 opacity-100"
          : "-translate-x-6 opacity-0 pointer-events-none"
      }`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        aria-label="See our flooring products in your room"
        data-testid="visualizer-launcher-btn"
        className="relative flex items-center gap-0 cursor-pointer select-none group/btn"
      >
        {/* Dark pill label — flush left edge */}
        <div className="bg-stone-900 text-stone-50 rounded-l-none rounded-r-2xl px-4 py-2.5 pr-6 -mr-4 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.4)]">
          <div className="font-display font-bold text-[13px] sm:text-[15px] leading-[1.15] max-w-[130px] sm:max-w-[150px]">
            See your
            <br />
            new floor
          </div>
        </div>

        {/* Glowing white circle with hand-holding-phone icon */}
        <div className="relative">
          {/* Orange glow ring behind circle */}
          <div
            aria-hidden
            className="absolute inset-0 rounded-full blur-md bg-orange-500 opacity-70 scale-110 group-hover/btn:opacity-90 transition-opacity"
          />
          <div
            aria-hidden
            className="absolute inset-0 rounded-full ring-2 ring-orange-500"
          />
          <div className="relative w-14 h-14 sm:w-16 sm:h-16 bg-stone-50 rounded-full flex items-center justify-center shadow-[0_10px_25px_-8px_rgba(234,88,12,0.55)] group-hover/btn:scale-105 transition-transform">
            {/* Hand holding phone with AR icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 40 40"
              fill="none"
              className="w-8 h-8 sm:w-9 sm:h-9"
              aria-hidden
            >
              {/* Phone body */}
              <rect
                x="12"
                y="4"
                width="15"
                height="24"
                rx="2.5"
                fill="#ffffff"
                stroke="#1c1917"
                strokeWidth="1.6"
              />
              {/* Screen border */}
              <rect
                x="14"
                y="6.5"
                width="11"
                height="17"
                rx="0.5"
                fill="#ffffff"
                stroke="#1c1917"
                strokeWidth="0.6"
              />
              {/* Home indicator */}
              <line
                x1="17"
                y1="26"
                x2="22"
                y2="26"
                stroke="#1c1917"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
              {/* AR reticle - orange corners */}
              <g stroke="#ea580c" strokeWidth="1.4" strokeLinecap="round" fill="none">
                <path d="M15.5 10 v-1 h1.2" />
                <path d="M23.5 10 v-1 h-1.2" />
                <path d="M15.5 20 v1 h1.2" />
                <path d="M23.5 20 v1 h-1.2" />
              </g>
              {/* Center + */}
              <g stroke="#ea580c" strokeWidth="1.4" strokeLinecap="round">
                <line x1="19.5" y1="14" x2="19.5" y2="16" />
                <line x1="18.5" y1="15" x2="20.5" y2="15" />
              </g>

              {/* Hand — thumb + fingers around phone */}
              <path
                d="M11 20 Q9 20 9 22 L9 30 Q9 33 12 33 L26 33 Q29 33 29 30 L29 22 Q29 20 27 20"
                fill="#1c1917"
                stroke="#1c1917"
                strokeWidth="1"
                strokeLinejoin="round"
              />
              {/* Thumb over phone */}
              <path
                d="M25.5 18 Q27 17 28 18 Q28.5 20 27 21 L26 21 Z"
                fill="#1c1917"
              />
              {/* Wrist */}
              <rect
                x="12"
                y="32"
                width="15"
                height="5"
                rx="1"
                fill="#1c1917"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Dismiss X */}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="visualizer-launcher-dismiss"
        className="absolute -top-2 right-2 w-5 h-5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shadow"
      >
        <X size={11} strokeWidth={2.5} />
      </button>
    </div>
  );
}
